import { fg } from '@confluence/feature-gating';
import {
	CFIND_5565_CREATION_FOCUSED_NAV_XFLOW,
	CREATION_FOCUSED_NAV_START_TIME,
	SIMPLIFY_NAV_NEW_USER,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';

import { getXflowExperimentStartDate } from './getXflowExperimentStartDate.ts';
import { isWithinD0Window } from './isWithinD0Window.ts';
import { preloadSpaceBlogCount } from './preloadSpaceBlogCount.ts';

export type OnboardingStateResult = {
	data: { onboardingState?: Array<{ key: string; value?: string | null }> | null } | null;
	hasErrors: boolean;
};

/**
 * Runs simplify-nav preload for space nav when the user is eligible (e.g. D0).
 * Currently preloads space blog count; call with the result of preloadSideNavigationOnboardingState().
 */
export async function preloadSimplifiedNavForSpaceNav(
	spaceKey: string,
	result: OnboardingStateResult,
): Promise<void> {
	const { data, hasErrors } = result;

	if (hasErrors || !data?.onboardingState) {
		return;
	}

	const eligibleEntry = data.onboardingState.find(
		(s) => s.key === SIMPLIFY_NAV_NEW_USER.NTH_USER_D0_SIMPLIFIED_NAV_ELIGIBLE,
	);
	const startAtEntry = data.onboardingState.find(
		(s) => s.key === SIMPLIFY_NAV_NEW_USER.NTH_USER_D0_SIMPLIFIED_NAV_START_AT,
	);

	if (eligibleEntry?.value !== 'true') {
		if (fg('cfind_5565_creation_focused_nav_xflow_preload')) {
			// Mirror the eligibility hook: do not preload if user is enrolled in the
			// old creation-focused-nav onboarding state.
			const creationFocusedNavStartTimeEntry = data.onboardingState.find(
				(s) => s.key === CREATION_FOCUSED_NAV_START_TIME,
			);
			if (creationFocusedNavStartTimeEntry?.value) {
				return;
			}

			// Not eligible for new-user experiment — check xflow eligibility.
			const xflowStartAtEntry = data.onboardingState.find(
				(s) => s.key === CFIND_5565_CREATION_FOCUSED_NAV_XFLOW.START_AT,
			);

			if (xflowStartAtEntry?.value) {
				// Returning enrolled user: preload if within D0 window.
				if (isWithinD0Window(xflowStartAtEntry.value)) {
					await preloadSpaceBlogCount(spaceKey);
				}
			} else {
				// First-time xflow user: no START_AT yet, but will get one on first render.
				// Preload optimistically if firstActivationDate is after the experiment start date.
				const rawActivationDate = getSSRRenderInputs().tapContext?.site?.firstActivationDate;
				const siteActivationDate = rawActivationDate ? String(rawActivationDate) : null;
				const experimentStartDate = getXflowExperimentStartDate();
				if (
					siteActivationDate &&
					experimentStartDate &&
					new Date(siteActivationDate) >= experimentStartDate
				) {
					await preloadSpaceBlogCount(spaceKey);
				}
			}
		}
		return;
	}

	// Relevant for real experiment variant users
	if (startAtEntry?.value) {
		if (isWithinD0Window(startAtEntry.value)) {
			await preloadSpaceBlogCount(spaceKey);
		}
	}
}
